<template>
    <div id="verify2Fa" class="verify-code-2fa">
        <FormError :hide-error="!errorCode" :error-code="$t(errorCode)"></FormError>

        <accountContainer class="narrow" :sub-title-text="$t('settings.verify-account')">
            <column>
                <p>
                    {{ $t('verify-mfa.desc') }} <br />
                    {{ $t('verify-mfa.desc-code') }}
                    <a target="_blank" rel="noopener noreferrer" :href="learnMoreLink" class="more">{{
                        $t('verify-mfa.more')
                    }}</a>
                </p>
                <div class="img">
                    <img v-if="isBackupCode" src="@/images/google-auth/verify-backup-code.png" />
                    <img v-else src="@/images/google-auth/verify-code-group_new.png" />
                </div>

                <div v-if="maskedEmail" class="maskedEmail text-center">
                    {{ maskedEmail }}
                </div>

                <FormCheckbox
                    id="auto-login"
                    v-model="rememberDevice"
                    name="auto-login"
                    :label-text="
                        $t(isLauncherMode ? 'login.verify-device.check-box-label' : 'verify-mfa.remember-device')
                    "
                >
                </FormCheckbox>

                <div class="input-container">
                    <authCodeInput
                        v-model="$v.inputCode.$model"
                        autofocus
                        :has-error="submitted && $v.inputCode.$error"
                        error-text="verify-mfa.error"
                        :length="codeLength"
                        @fulfill="verifyCode"
                    />

                    <div class="clearfix"></div>

                    <!-- <reCaptcha
                        v-if="showCaptcha"
                        v-model="$v.reCaptchaCode.$model"
                        :has-error="submitted && $v.reCaptchaCode.$error"
                        :override-error-text="$t('error.form.complete-captcha')"
                        @captcha-completed="reCaptchaCode = true"
                    >
                    </reCaptcha> -->
                </div>

                <FormButton
                    id="verify"
                    data-cy="submit"
                    class="btn-gradient"
                    type="submit"
                    :loading="buttonLoading"
                    :button-click="verifyCode"
                    :label="$t('verify')"
                >
                </FormButton>

                <i18n path="create-account.complete.or-continue" tag="p" class="link-cancel text-center">
                    <template v-slot:continueLink>
                        <router-link id="cancel" :to="{ name: 'login' }">
                            {{ $t('cancel') }}
                        </router-link>
                    </template>
                </i18n>
                <ul class="help list-unstyled">
                    <li>
                        <router-link
                            class="link-another-method"
                            :to="{ name: 'auth-types-2fa-login', params: { byTPALogin } }"
                        >
                            <svgIcon icon-holder-class="icon-help" icon-id="icon-page-right" />
                            {{ $t('verify-mfa.use-another-method') }}
                        </router-link>
                    </li>
                </ul>
            </column>
        </accountContainer>
    </div>
</template>

<script>
import { required, minLength } from 'vuelidate/lib/validators';
import { FormError, FormButton, FormCheckbox } from '@arena/toolkit';
// import reCaptcha from '@/components/recaptcha';
import authCodeInput from '@/components/global/auth-code-input';
import accountContainer from '@/components/account-container';
import column from '@/components/column-container';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import autoSubmit from '@/mixins/autoSubmit';
import learnMoreLink from '@/config/2fa-links';

export default {
    name: 'VerifyCode2faLogin',
    components: {
        accountContainer,
        FormError,
        FormButton,
        FormCheckbox,
        // reCaptcha,
        column,
        svgIcon,
        authCodeInput,
    },
    metaInfo() {
        return {
            titleTemplate: `%s | ${this.$t('login.login')}`,
        };
    },
    mixins: [autoSubmit],
    props: {
        isBackupCode: {
            type: Boolean,
            default: false,
        },
        byTPALogin: {
            type: Boolean,
            default: false,
        },
        maskedEmail: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            errorCode: '',
            inputCode: '',
            buttonLoading: false,
            rememberDevice: false,
            // showCaptcha: false,
            // recaptcha: {
            //     action: {
            //         web: 'web_login_2fa',
            //         launcher: 'launcher_login_2fa',
            //     },
            // },
            // reCaptchaCode: '',
            submitted: false,
        };
    },
    computed: {
        codeLength() {
            return this.isBackupCode ? 8 : 6;
        },
        learnMoreLink() {
            return learnMoreLink[this.region].learnMore.authApp;
        },
        isLauncherMode() {
            return this.$store.state.isApplication;
        },
    },
    created() {
        if (!(this.byTPALogin || (this.$store.state.login.loginId && this.$store.state.login.password))) {
            this.$router.replace({ name: 'login' });
        } else {
            dataLayer.push({
                event: 'formFieldFocus',
                formName: this.$route.name,
            });
        }
    },
    validations() {
        return {
            inputCode: {
                required,
                minLength: minLength(this.codeLength),
            },
        };
    },
    methods: {
        setError(error) {
            this.buttonLoading = false;
            this.errorCode = error;
            this.submitted = false;
        },
        submit() {
            this.buttonLoading = true;
            this.errorCode = '';
        },
        verifyCode() {
            this.$v.$touch();
            this.submitted = true;
            if (!this.$v.$invalid) {
                this.submit();

                this.$store
                    .dispatch('login/verify', {
                        authType: 1,
                        code: this.inputCode,
                        rememberMe: this.rememberDevice,
                        byTPALogin: this.byTPALogin,
                    })
                    .then(() => {
                        this.trackAnalytics(this.rememberDevice ? 'yes' : 'no');
                        dataLayer.push({
                            event: 'formSubmit',
                            formName: this.$route.name,
                            formSubmitSuccess: 'True',
                            rememberState: this.rememberDevice ? 'Remembered' : 'Not Remembered',
                        });
                    })
                    .catch(({ errorCode }) => {
                        dataLayer.push({
                            event: 'formSubmit',
                            formName: this.$route.name,
                            formSubmitSuccess: 'False',
                            rememberState: this.rememberDevice ? 'Remembered' : 'Not Remembered',
                        });
                        this.trackAnalyticsCodeInvalid(
                            this.rememberDevice ? 'yes' : 'no',
                            this.$t('error.form.' + errorCode),
                            errorCode
                        );
                        // if (this.showCaptcha) {
                        //     this.reCaptchaCode = '';
                        //     window.grecaptcha.enterprise.reset(window.rcWidgetId);
                        // }
                        // if (errorCode === 20187) {
                        //     this.showCaptcha = true;
                        //     errorCode = 20188;
                        // }
                        this.setError(errorCode);
                    });
            }
        },
        trackAnalytics(rememberSetting) {
            dataLayer.push({
                event: '2FAlogin',
                codeLength: this.inputCode.length,
                codeType: 'Standard',
                rememberSettings: rememberSetting,
                '2FAType': 'App',
            });
        },
        trackAnalyticsCodeInvalid(rememberSetting, errorMessage, errorCode) {
            dataLayer.push({
                event: '2FA Login Error',
                codeLength: this.inputCode.length,
                codeType: 'Standard',
                rememberSettings: rememberSetting,
                errorText: errorMessage, //The error text, in English if possible
                errorID: errorCode, //the error identifier
                '2FAType': 'App',
            });
        },
    },
};
</script>

<style lang="less" scoped>
.contents.narrow {
    padding-left: 0;
    padding-right: 0;
    width: 320px;
    position: relative;

    .maskedEmail {
        color: white;
        font-size: 13px;
        margin-bottom: 20px;
    }
}
.more {
    color: rgba(247, 247, 247, 0.4);
    display: block;
    text-decoration: underline;
    &:hover {
        color: rgba(247, 247, 247, 0.4);
    }
}
.img {
    height: 160px;
    width: 100%;
    img {
        height: 100%;
        width: 100%;
    }
}
.router-link-active {
    text-decoration: underline;
}

.link-another-method {
    cursor: pointer;
}
</style>
